import React, { createContext, useContext } from "react";
import {
    IAppSettings,
    IProject,
    IProjectResults,
    IProjectHourTotals,
    ITimeEntry,
    IUser,
    User,
    IDefaultHoursConfig,
    IDefaultHoursAssignment,
    IDefaultHoursConsumption,
    IDefaultHoursDay,
    IDefaultHoursConfiguration,
} from "../types/types";
import { AlertContext } from "./AlertProvider";
import { webLightTheme } from "@fluentui/react-components";
import ReloadButton from "../components/ReloadButton";
import { armDebugExpiredOnNextRequest, consumeDebugExpiredOnNextRequest, renewAuthToken } from "../utils/tokenBridge";

const baseUrl = process.env.REACT_APP_APIURL;
const apiVersion = "/api/v1";
export const authUrl = `${baseUrl}${apiVersion}/auth`;
export const projectUrl = `${baseUrl}${apiVersion}/project`;
export const userUrl = `${baseUrl}${apiVersion}/user`;
export const companyUrl = `${baseUrl}${apiVersion}/company`;
export const timeEntryUrl = `${baseUrl}${apiVersion}/timeEntry`;
export const defaultHoursUrl = `${baseUrl}${apiVersion}/defaultHours`;
export const adminDefaultHoursUrl = `${baseUrl}${apiVersion}/admin/default-hours`;
export interface ICompany {
    id: string; // Database id
    company_name: string; // Company name
    locked: boolean; // Is the company locked?
}

export interface IProjectCompnayID {
    project_name: string;
    company_id: string;
    project_id: string;
}

export interface IHasHoursResponse {
    companies: string[];
    projects: string[];
    projectCompanyID: IProjectCompnayID[];
}

export interface ICompanyInfoObject {
    company_name: string;
    id: string;
    locked: boolean;
}

export interface IFetchHoursBody {
    company?: string;
    projects?: any[];
    from?: number;
    to?: number;
    getAll?: boolean;
    users?: any[];
    /** Optional flag to exclude the "Offcode - Default Hours" project from reports */
    excludeDefaultProject?: boolean;
}

interface IContextProviderProps {
    children: React.ReactNode;
}

interface IApiContext {
    getServerSideToken: (token: string, tid: string) => Promise<string>;
    getServerSideVersion: () => Promise<{ version: string }>;
    fetchHours: (token: string, body: any) => Promise<ArrayBuffer>;
    fetchYearly: (token: string, body: any) => Promise<ArrayBuffer>;
    fetchHasHours: (token: string) => Promise<IHasHoursResponse>;
    getProjects: (token: string, id: any) => Promise<IProject[]>;
    getCompanies: (token: string) => Promise<ICompany[]>;
    createCompany: (token: string, body: { company_name: any; locked: boolean }) => Promise<ICompany>;
    getCompanyByName: (token: string, name: string) => Promise<ICompanyInfoObject>;
    getUsers: (token: string) => Promise<IUser[]>;
    updateUserAppSettings: (token: string, app_settings: IAppSettings) => Promise<User>;
    setNewUser: (token: any, body: any) => Promise<void>;
    getUser: (token: string) => Promise<User>;
    getTimeEntriesBetween: (token: string, date: { from: any; to: any }) => Promise<any>;
    /** Per-project month + year hour totals for the current user */
    getProjectHourTotals: (token: string, year: number, month: number) => Promise<IProjectHourTotals>;
    submitTimeEntry: (token: string, body: ITimeEntry) => Promise<void>;
    updateTimeEntry: (token: string, body: object, id: string) => Promise<void>;
    updateDescription: (token: string, body: { description: any }, id: string | undefined) => Promise<void>;
    deleteTimeEntry: (token: string, id: any, from: any) => Promise<void>;
    /** Reset default hours for a day based on a specific default-hours time entry */
    resetDefaultHoursForTimeEntry: (token: string, timeEntryId: string) => Promise<{ message: string; day: IDefaultHoursDay }>;
    fetchProjects: (token: string) => Promise<IProjectResults[]>;
    fetchProject: (token: any, companyID: any, projectName: any) => Promise<IProject>;
    deleteProject: (token: any, id: any) => Promise<void>;
    getProjectSettings: (token: string, name: string) => Promise<IProject[]>;
    getProjectById: (token: string, id: string | undefined) => Promise<IProject>;
    updateProjectSettings: (token: string, body: IProject, id: string | undefined) => Promise<IProject>;
    createProject: (token: string, body: IProject) => Promise<IProject>;
    importHours: (token: string, body: { userData: object }) => Promise<void>;
    getDefaultHoursConfig: (token: string) => Promise<IDefaultHoursConfig>;
    getDefaultHoursConfigForUser: (token: string, userId: string) => Promise<IDefaultHoursAssignment[]>;
    createDefaultHoursAssignment: (token: string, body: IDefaultHoursAssignment) => Promise<IDefaultHoursAssignment>;
    updateDefaultHoursAssignment: (token: string, body: IDefaultHoursAssignment, id: string) => Promise<IDefaultHoursAssignment>;
    deleteDefaultHoursAssignment: (token: string, id: string) => Promise<void>;
    getDefaultHoursConsumption: (token: string, dateFrom: number, dateTo: number) => Promise<IDefaultHoursConsumption[]>;
    // Get all default hours days in a given range [from, to] (week-level helper)
    getDefaultHoursWeek: (token: string, dateFrom: number, dateTo: number) => Promise<IDefaultHoursDay[]>;
    // Returns null when default hours day does not exist (backend 404)
    getDefaultHoursDay: (token: string, date: number) => Promise<IDefaultHoursDay | null>;
    // Get active default-hours configuration for the current user on a specific date (frontend "default project" source of truth)
    getDefaultHoursConfigurationForDate: (token: string, date: number) => Promise<IDefaultHoursConfiguration | null>;
    /** Active default-hours configuration per day in [from, to] (keys: start-of-day UTC ms as string). */
    getDefaultHoursConfigurationForWeek: (
        token: string,
        dateFrom: number,
        dateTo: number,
    ) => Promise<Record<string, IDefaultHoursConfiguration | null>>;
    setDefaultHoursDayMode: (token: string, date: number, mode: "auto" | "manual") => Promise<IDefaultHoursDay>;
    overrideDefaultHoursForDay: (token: string, date: number, totalHours: number) => Promise<IDefaultHoursDay>;
    resetDefaultHoursOverrideForDay: (token: string, date: number) => Promise<IDefaultHoursDay>;
    /** Re-enable auto-update for default hours on a specific day (clears manual modifications). */
    resetDefaultHoursAutoUpdate: (token: string, date: number) => Promise<void>;
    /** Debug helper: simulate an expired token response from backend (dev only). */
    debugSimulateTokenExpired: (token: string) => Promise<void>;
}

export const ApiContext = createContext<IApiContext>({
    getServerSideToken: async () => {
        return "";
    },
    getServerSideVersion: async () => {
        return { version: "" };
    },
    fetchHours: async () => new ArrayBuffer(0),
    fetchYearly: async () => new ArrayBuffer(0),
    fetchHasHours: async () => {
        return {} as IHasHoursResponse;
    },
    getProjects: async () => [],
    getCompanies: async () => [],
    createCompany: async () => {
        return {} as ICompany;
    },
    getCompanyByName: async () => {
        return {} as ICompanyInfoObject;
    },
    getUsers: async () => [],
    updateUserAppSettings: async () => {
        return {} as User;
    },
    setNewUser: async () => {},
    getUser: async () => {
        return {} as User;
    },
    getTimeEntriesBetween: async () => {},
    getProjectHourTotals: async () => ({ year: 0, month: 0, totals: {} }),
    submitTimeEntry: async () => {},
    updateTimeEntry: async () => {},
    updateDescription: async () => {},
    deleteTimeEntry: async () => {},
    resetDefaultHoursForTimeEntry: async () => {
        return { message: "", day: {} as IDefaultHoursDay };
    },
    fetchProjects: async () => [],
    fetchProject: async () => {
        return {} as IProject;
    },
    deleteProject: async () => {},
    getProjectSettings: async () => {
        return [] as IProject[];
    },
    getProjectById: async () => {
        return {} as IProject;
    },
    updateProjectSettings: async () => {
        return {} as IProject;
    },
    createProject: async () => {
        return {} as IProject;
    },
    importHours: async () => {},
    getDefaultHoursConfig: async () => {
        return {} as IDefaultHoursConfig;
    },
    getDefaultHoursConfigForUser: async () => {
        return [];
    },
    createDefaultHoursAssignment: async () => {
        return {} as IDefaultHoursAssignment;
    },
    updateDefaultHoursAssignment: async () => {
        return {} as IDefaultHoursAssignment;
    },
    deleteDefaultHoursAssignment: async () => {},
    getDefaultHoursConsumption: async () => {
        return [];
    },
    getDefaultHoursWeek: async () => {
        return [];
    },
    getDefaultHoursDay: async () => {
        // Default implementation: no default hours day
        return null;
    },
    getDefaultHoursConfigurationForDate: async () => {
        // Default implementation: no active configuration
        return null;
    },
    getDefaultHoursConfigurationForWeek: async () => {
        return {};
    },
    setDefaultHoursDayMode: async () => {
        return {} as IDefaultHoursDay;
    },
    overrideDefaultHoursForDay: async () => {
        return {} as IDefaultHoursDay;
    },
    resetDefaultHoursOverrideForDay: async () => {
        return {} as IDefaultHoursDay;
    },
    resetDefaultHoursAutoUpdate: async () => {},
    debugSimulateTokenExpired: async () => {},
});

function isRenewableAuthError(error: unknown): boolean {
    if (!error || typeof error !== "object") {
        return false;
    }
    const err = error as { status?: number; error?: string };
    if (err.status === 401) {
        return true;
    }
    const message = typeof err.error === "string" ? err.error.toLowerCase() : "";
    return message.includes("token expired") || message.includes("token_expired");
}

export function ApiProvider({ children }: IContextProviderProps) {
    const { setErrorMsg } = useContext(AlertContext);
    const theme = webLightTheme;
    // import { baseUrl } from "../utils/constants";
    const getHeader = async (token: string) => {
        return {
            "Content-Type": "application/json",
            Accept: "application/json",
            Authorization: "Bearer " + token,
            "x-frontend-version": `${process.env.REACT_APP_VERSION}`,
            // "Access-Control-Allow-Origin": `${baseUrl}`,
        };
    };

    /**
     * Wrapper function that handles token renewal and retry on 401 errors
     * @param apiCall Function that makes the API call
     * @param token Current token
     * @param renewTokenFn Optional function to renew the token
     * @param retryCount Number of retries (default: 1)
     * @returns Promise with the API response
     */
    const showSessionExpiredError = () => {
        setErrorMsg(
            <>
                <p> Session expired or authentication failed. </p>
                <p> Please reload this tab or reopen Time Tracker in Teams. If the problem persists, contact Toni K.</p>
                <p>
                    <strong> Error: 401 </strong>
                </p>
                <ReloadButton />
            </>,
        );
    };

    const withTokenRenewal = async <T,>(
        apiCall: (token: string) => Promise<T>,
        token: string,
        renewTokenFn?: () => Promise<string>,
        retryCount: number = 1,
    ): Promise<T> => {
        try {
            // Dev-only: pretend the token expired on the next real API call (e.g. after debug button)
            if (consumeDebugExpiredOnNextRequest()) {
                throw { error: "Token expired", status: 401 };
            }
            return await apiCall(token);
        } catch (error: unknown) {
            if (isRenewableAuthError(error) && retryCount > 0 && renewTokenFn) {
                console.log("Token expired, renewing token and retrying request...");
                try {
                    const newToken = await renewTokenFn();
                    return await apiCall(newToken);
                } catch (renewError) {
                    console.error("Failed to renew token:", renewError);
                    showSessionExpiredError();
                    throw error;
                }
            }
            if (isRenewableAuthError(error)) {
                showSessionExpiredError();
            }
            throw error;
        }
    };

    /** Run fetch + handleErrors with automatic token renewal on 401 */
    const withAuth = <T,>(token: string, apiCall: (currentToken: string) => Promise<T>): Promise<T> => {
        return withTokenRenewal(apiCall, token, renewTokenHelper);
    };

    const handleAuthResponse = async (response: Response): Promise<any> => {
        if (!response.ok && response.status === 401) {
            const errorData = await response.json().catch(() => ({ error: response.statusText, status: 401 }));
            throw errorData;
        }
        return handleErrors(response);
    };

    /**
     * Handle errors from backend
     * @param response Response from backend
     * @returns Promise with response
     */
    function handleErrors(response: Response): any {
        return new Promise<void>((resolve, reject) => {
            if (!response.ok) {
                if (response.status === 409) {
                    setErrorMsg(
                        <>
                            <p> Frontend and backend version mismatch! </p>
                            <p> Please refresh the page. If the problem persists, contact Toni K.</p>
                            <p>
                                <strong> Error: {response.status} </strong>
                                <br />
                                {response.statusText}
                            </p>
                            <ReloadButton />
                        </>,
                    );
                } else if (response.status === 401) {
                    // Do not show blocking UI here — withTokenRenewal / withAuth renew and retry first.
                    response
                        .json()
                        .then((body) => reject({ error: body?.error ?? response.statusText, status: 401 }))
                        .catch(() => reject({ error: response.statusText, status: 401 }));
                    return;
                } else if (response.status === 403) {
                    setErrorMsg(
                        <>
                            <p> Forbidden! </p>
                            <p> You don&apos;t have permission to view this page. </p>
                            <p>
                                <strong> Error: {response.status} </strong>
                                <br />
                                {response.statusText}
                            </p>
                            <ReloadButton />
                        </>,
                    );
                } else if (response.status === 404) {
                    setErrorMsg(
                        <>
                            <p> Not found! </p>
                            <p> The page you are looking for does not exist. </p>
                            <p>
                                <strong> Error: {response.status} </strong>
                                <br />
                                {response.statusText}
                            </p>
                            <ReloadButton />
                        </>,
                    );
                } else {
                    setErrorMsg(
                        <>
                            <p> Something went wrong! </p>
                            <p> Please refresh the page. If the problem persists, contact Toni K.</p>
                            <p>
                                <strong> Error: {response.status} </strong>
                                <br />
                                {response.statusText}
                            </p>
                            <ReloadButton />
                        </>,
                    );
                }

                return reject(response);
            }

            if (response.status === 204) {
                return resolve();
            }

            return response.json().then(resolve).catch(resolve);
        });
    }

    /** Fetch serverside token (AAD) */
    const getServerSideToken = async (token: string, tid: string): Promise<string> => {
        return withAuth(token, async (currentToken) => {
            const response = await fetch(`${authUrl}/aadToken`, {
                method: "POST",
                headers: await getHeader(currentToken),
                body: JSON.stringify({
                    tid: tid,
                    token: currentToken,
                }),
                mode: "cors",
                cache: "default",
            });
            return handleAuthResponse(response);
        });
    };

    /** Get server side version */
    const getServerSideVersion = async () => {
        return fetch(`${baseUrl}/api/v1/versionNumber`, {
            method: "GET",
            headers: {
                "Content-Type": "application/json",
                Accept: "application/json",
            },
            mode: "cors",
            cache: "default",
        }).then(handleErrors);
    };

    /** Renew token via ContextProvider (Teams SDK) and clear any stale auth error overlay */
    const renewTokenHelper = async (): Promise<string> => {
        const newToken = await renewAuthToken();
        setErrorMsg(undefined);
        return newToken;
    };

    /** Fetch hours from DB and save file into oneDrive */
    const fetchHours = async (token: string, body: IFetchHoursBody) => {
        return withTokenRenewal(
            async (currentToken: string) => {
                const response = await fetch(`${projectUrl}/reports/hours`, {
                    method: "POST",
                    headers: await getHeader(currentToken),
                    body: JSON.stringify(body),
                    mode: "cors",
                    cache: "default",
                });

                if (!response.ok) {
                    const errorData = await response.json().catch(() => ({ error: response.statusText }));
                    throw errorData;
                }

                return response.arrayBuffer();
            },
            token,
            renewTokenHelper,
        );
    };

    /** Fetch yearly total for all Offcode users & companies from DB and save file into oneDrive */
    const fetchYearly = async (token: string, body: IFetchHoursBody): Promise<ArrayBuffer> => {
        return withTokenRenewal(
            async (currentToken: string) => {
                const response = await fetch(`${projectUrl}/reports/yearlyTotal`, {
                    method: "POST",
                    headers: await getHeader(currentToken),
                    body: JSON.stringify(body),
                    mode: "cors",
                    cache: "default",
                });

                if (!response.ok) {
                    const errorData = await response.json().catch(() => ({ error: response.statusText }));
                    throw errorData;
                }

                return response.arrayBuffer();
            },
            token,
            renewTokenHelper,
        );
    };

    /** Fetch companies / projects that has time entries for current user  */
    const fetchHasHours = async (token: string): Promise<IHasHoursResponse> => {
        return withAuth(token, async (currentToken) => {
            const response = await fetch(`${projectUrl}/hasHours`, {
                method: "GET",
                headers: await getHeader(currentToken),
            });
            return handleAuthResponse(response);
        });
    };

    /** Fetch projects based on company id */
    const getProjects = async (token: string, id: any): Promise<IProject[]> => {
        const encodedId = encodeURIComponent(id);
        return withAuth(token, async (currentToken) => {
            const response = await fetch(`${projectUrl}/withCompanyId/${encodedId}`, {
                method: "GET",
                headers: await getHeader(currentToken),
            });
            return handleAuthResponse(response);
        });
    };

    /** Fetch all companies */
    const getCompanies = async (token: string): Promise<ICompany[]> => {
        return withAuth(token, async (currentToken) => {
            const response = await fetch(`${companyUrl}`, {
                method: "GET",
                headers: await getHeader(currentToken),
            });
            return handleAuthResponse(response);
        });
    };

    /** Create company */
    const createCompany = async (token: string, body: { company_name: any; locked: boolean }) => {
        return withAuth(token, async (currentToken) => {
            const response = await fetch(`${companyUrl}`, {
                method: "POST",
                headers: await getHeader(currentToken),
                body: JSON.stringify(body),
            });
            return handleAuthResponse(response);
        });
    };

    /** Fetch company settings based on company name */
    const getCompanyByName = async (token: string, name: string): Promise<ICompanyInfoObject> => {
        return withAuth(token, async (currentToken) => {
            const response = await fetch(`${companyUrl}/name/${name}`, {
                method: "GET",
                headers: await getHeader(currentToken),
            });
            return handleAuthResponse(response);
        });
    };

    /** Fetch Users */
    const getUsers = async (token: string): Promise<IUser[]> => {
        return withAuth(token, async (currentToken) => {
            const response = await fetch(`${userUrl}/?all`, {
                method: "GET",
                headers: await getHeader(currentToken),
            });
            return handleAuthResponse(response);
        });
    };

    /** Update User app_settings with automatic token renewal */
    const updateUserAppSettings = async (token: string, app_settings: IAppSettings): Promise<User> => {
        return withTokenRenewal(
            async (currentToken: string) => {
                // Backend schema no longer allows legacy default_project fields on app_settings.
                // Sanitize payload to avoid 400 "must NOT have additional properties" errors.
                const sanitizedSettings: any = {
                    ...app_settings,
                };

                if ("default_project" in sanitizedSettings) {
                    delete sanitizedSettings.default_project;
                }
                if ("default_project_string" in sanitizedSettings) {
                    delete sanitizedSettings.default_project_string;
                }

                const response = await fetch(`${userUrl}/app_settings`, {
                    method: "PUT",
                    headers: await getHeader(currentToken),
                    body: JSON.stringify(sanitizedSettings),
                });

                if (!response.ok && response.status === 401) {
                    const errorData = await response.json().catch(() => ({ error: response.statusText, status: response.status }));
                    throw errorData;
                }

                return handleErrors(response);
            },
            token,
            renewTokenHelper,
        );
    };

    /** Set new user */
    const setNewUser = async (token: any, body: any) => {
        return withAuth(token, async (currentToken) => {
            const response = await fetch(`${userUrl}`, {
                method: "POST",
                headers: await getHeader(currentToken),
                body: JSON.stringify(body),
            });
            return handleAuthResponse(response);
        });
    };

    /** Fetch User with automatic token renewal on "Token expired" */
    const getUser = async (token: string): Promise<User> => {
        return withTokenRenewal(
            async (currentToken: string) => {
                const response = await fetch(`${userUrl}`, {
                    method: "GET",
                    headers: await getHeader(currentToken),
                });

                // For token-expired 401s, throw a structured error so withTokenRenewal can handle it
                if (!response.ok && response.status === 401) {
                    const errorData = await response.json().catch(() => ({ error: response.statusText, status: response.status }));
                    throw errorData;
                }

                return handleErrors(response);
            },
            token,
            renewTokenHelper,
        );
    };

    // /** Get weektotal */
    // const getWeekTotal = async (token, from, to) => {
    //     return fetch(
    //         `${timeEntryUrl}/weekTotal/${from}/${to}`,
    //         {
    //             method: "GET",
    //             headers: await getHeader(token),
    //         }
    //     )
    //         .then(handleErrors)
    //         .catch((err) => {
    //
    //             throw err;
    //         });
    // };

    /** Get timeEntry (from - to) with automatic token renewal on "Token expired" */
    const getTimeEntriesBetween = async (token: string, date: { from: any; to: any }) => {
        const now = new Date();
        return withTokenRenewal(
            async (currentToken: string) => {
                const response = await fetch(`${timeEntryUrl}/${date.from}/${date.to}`, {
                    method: "GET",
                    headers: await getHeader(currentToken),
                });

                // For token-expired 401s, throw a structured error so withTokenRenewal can handle it
                if (!response.ok && response.status === 401) {
                    const errorData = await response.json().catch(() => ({ error: response.statusText, status: response.status }));
                    throw errorData;
                }

                const diff = new Date().getTime() - now.getTime();
                console.debug("Time took to fetch hours", diff);
                return handleErrors(response);
            },
            token,
            renewTokenHelper,
        );
    };

    /** Get per-project month + year hour totals for the authenticated user */
    const getProjectHourTotals = async (token: string, year: number, month: number): Promise<IProjectHourTotals> => {
        return withTokenRenewal(
            async (currentToken: string) => {
                const response = await fetch(`${timeEntryUrl}/projectTotals/${year}/${month}`, {
                    method: "GET",
                    headers: await getHeader(currentToken),
                });

                if (!response.ok && response.status === 401) {
                    const errorData = await response.json().catch(() => ({ error: response.statusText, status: response.status }));
                    throw errorData;
                }

                return handleErrors(response);
            },
            token,
            renewTokenHelper,
        );
    };

    // /** Get hours (from - to) */
    // const getHoursBetween = async (token, date) => {
    //     return fetch(
    //         `${timeEntryUrl}/hours/${date.from}/${date.to}`,
    //         {
    //             method: "GET",
    //             headers: await getHeader(token),
    //         }
    //     )
    //         .then(handleErrors)
    //         .catch((err) => {
    //
    //             throw err;
    //         });
    // };

    /** Submit time entry into DB with automatic token renewal */
    const submitTimeEntry = async (token: string, body: ITimeEntry) => {
        return withTokenRenewal(
            async (currentToken: string) => {
                const response = await fetch(`${timeEntryUrl}`, {
                    method: "POST",
                    headers: await getHeader(currentToken),
                    body: JSON.stringify(body),
                });

                if (!response.ok && response.status === 401) {
                    const errorData = await response.json().catch(() => ({ error: response.statusText, status: response.status }));
                    throw errorData;
                }

                return handleErrors(response);
            },
            token,
            renewTokenHelper,
        );
    };

    /** Update existing time entry with automatic token renewal */
    const updateTimeEntry = async (token: string, body: object, id: string) => {
        return withTokenRenewal(
            async (currentToken: string) => {
                const response = await fetch(`${timeEntryUrl}/` + id, {
                    method: "PUT",
                    headers: await getHeader(currentToken),
                    body: JSON.stringify(body),
                });

                if (!response.ok && response.status === 401) {
                    const errorData = await response.json().catch(() => ({ error: response.statusText, status: response.status }));
                    throw errorData;
                }

                return handleErrors(response);
            },
            token,
            renewTokenHelper,
        );
    };

    /** Update existing time entry description with automatic token renewal */
    const updateDescription = async (token: string, body: { description: any }, id: string | undefined) => {
        return withTokenRenewal(
            async (currentToken: string) => {
                const response = await fetch(`${timeEntryUrl}/description/` + id, {
                    method: "PATCH",
                    headers: await getHeader(currentToken),
                    body: JSON.stringify(body),
                    mode: "cors",
                });

                if (!response.ok && response.status === 401) {
                    const errorData = await response.json().catch(() => ({ error: response.statusText, status: response.status }));
                    throw errorData;
                }

                return handleErrors(response);
            },
            token,
            renewTokenHelper,
        );
    };

    /** Delete timeEntry with automatic token renewal */
    const deleteTimeEntry = async (token: string, id: any, from: any) => {
        return withTokenRenewal(
            async (currentToken: string) => {
                const response = await fetch(`${timeEntryUrl}/${id}/${from}`, {
                    method: "DELETE",
                    headers: await getHeader(currentToken),
                });

                if (!response.ok && response.status === 401) {
                    const errorData = await response.json().catch(() => ({ error: response.statusText, status: response.status }));
                    throw errorData;
                }

                return handleErrors(response);
            },
            token,
            renewTokenHelper,
        );
    };

    /**
     * Reset default hours back to auto mode for the given time entry's day.
     * Uses POST /timeEntry/:id/reset-default-hours and returns the updated day info.
     */
    const resetDefaultHoursForTimeEntry = async (
        token: string,
        timeEntryId: string,
    ): Promise<{ message: string; day: IDefaultHoursDay }> => {
        return withTokenRenewal(
            async (currentToken: string) => {
                const response = await fetch(`${timeEntryUrl}/${timeEntryId}/reset-default-hours`, {
                    method: "POST",
                    headers: await getHeader(currentToken),
                });

                if (!response.ok && response.status === 401) {
                    const errorData = await response.json().catch(() => ({ error: response.statusText, status: response.status }));
                    throw errorData;
                }

                return handleErrors(response);
            },
            token,
            renewTokenHelper,
        );
    };

    /** Get projects (for user) */
    const fetchProjects = async (token: string) => {
        return withAuth(token, async (currentToken) => {
            const response = await fetch(`${projectUrl}`, {
                method: "GET",
                headers: await getHeader(currentToken),
            });
            return handleAuthResponse(response);
        });
    };

    /** Get projects based on company_id & projectName */
    const fetchProject = async (token: any, companyID: any, projectName: any) => {
        return withAuth(token, async (currentToken) => {
            const response = await fetch(`${projectUrl}/byID/${companyID}/${projectName}`, {
                method: "GET",
                headers: await getHeader(currentToken),
            });
            return handleAuthResponse(response);
        });
    };

    /** Delete project based on id */
    const deleteProject = async (token: any, id: any) => {
        return withAuth(token, async (currentToken) => {
            const response = await fetch(`${projectUrl}/${id}`, {
                method: "DELETE",
                headers: await getHeader(currentToken),
            });
            return handleAuthResponse(response);
        });
    };

    /** Fetch project setting based on project name*/
    const getProjectSettings = async (token: string, name: string): Promise<IProject[]> => {
        return withAuth(token, async (currentToken) => {
            const response = await fetch(`${projectUrl}/byName/${name}`, {
                method: "GET",
                headers: await getHeader(currentToken),
            });
            return handleAuthResponse(response);
        });
    };

    //Get project by id
    const getProjectById = async (token: string, id: string | undefined) => {
        return withAuth(token, async (currentToken) => {
            const response = await fetch(`${projectUrl}/withId/${id}`, {
                method: "GET",
                headers: await getHeader(currentToken),
            });
            return handleAuthResponse(response);
        });
    };

    /** Update existing project settings */
    const updateProjectSettings = async (token: string, body: IProject, id: string | undefined) => {
        return withAuth(token, async (currentToken) => {
            const response = await fetch(`${projectUrl}/` + id, {
                method: "PUT",
                headers: await getHeader(currentToken),
                body: JSON.stringify(body),
            });
            return handleAuthResponse(response);
        });
    };

    /** Create project */
    const createProject = async (token: string, body: IProject): Promise<IProject> => {
        return withAuth(token, async (currentToken) => {
            const response = await fetch(`${projectUrl}`, {
                method: "POST",
                headers: await getHeader(currentToken),
                body: JSON.stringify(body),
            });
            return handleAuthResponse(response);
        });
    };

    /**Import hours from file */
    const importHours = async (token: string, body: { userData: object }) => {
        return withAuth(token, async (currentToken) => {
            const response = await fetch(`${companyUrl}/importTimeTable`, {
                method: "POST",
                headers: await getHeader(currentToken),
                body: JSON.stringify(body),
            });
            return handleAuthResponse(response);
        });
    };

    /** Get default hours configuration (admin only) */
    const getDefaultHoursConfig = async (token: string): Promise<IDefaultHoursConfig> => {
        return withAuth(token, async (currentToken) => {
            const response = await fetch(`${defaultHoursUrl}`, {
                method: "GET",
                headers: await getHeader(currentToken),
            });
            return handleAuthResponse(response);
        });
    };

    /** Get default hours configuration for a specific user (admin-only endpoint)
     *  Normalizes different backend shapes to a plain array of `IDefaultHoursAssignment`.
     *
     *  Supported backend responses:
     *  - `IDefaultHoursAssignment[]`
     *  - `{ assignments: IDefaultHoursAssignment[] }`
     *  - Single configuration object from `defaultHoursConfigurations` table:
     *      {
     *        id, user_oid, project_id, default_hours,
     *        date_range: { from, to }, created, modified, created_by
     *      }
     */
    const getDefaultHoursConfigForUser = async (token: string, userId: string): Promise<IDefaultHoursAssignment[]> => {
        const result = await withAuth(token, async (currentToken) => {
            const response = await fetch(`${baseUrl}${apiVersion}/admin/default-hours/user/${userId}`, {
                method: "GET",
                headers: await getHeader(currentToken),
            });
            return handleAuthResponse(response);
        });

        // Case 1: backend already returns an array of assignments
        if (Array.isArray(result)) {
            return result as IDefaultHoursAssignment[];
        }

        // Case 2: wrapper object with `assignments` array
        if (result && Array.isArray((result as any).assignments)) {
            return (result as any).assignments as IDefaultHoursAssignment[];
        }

        // Case 3: single configuration object from `defaultHoursConfigurations` table
        if (result && typeof result === "object" && "user_oid" in result && "default_hours" in result && "project_id" in result) {
            const cfg: any = result;
            const assignment: IDefaultHoursAssignment = {
                id: cfg.id ?? undefined,
                user_oid: userId,
                project_id: cfg.project_id,
                default_hours: cfg.default_hours,
                date_range: {
                    from: cfg.date_range?.from ?? null,
                    to: cfg.date_range?.to ?? null,
                },
                created: cfg.created,
                modified: cfg.modified,
            };
            return [assignment];
        }

        return [];
    };

    /** Create default hours assignment (admin only) */
    const createDefaultHoursAssignment = async (token: string, body: IDefaultHoursAssignment): Promise<IDefaultHoursAssignment> => {
        return withAuth(token, async (currentToken) => {
            const response = await fetch(`${adminDefaultHoursUrl}`, {
                method: "POST",
                headers: await getHeader(currentToken),
                body: JSON.stringify(body),
            });
            return handleAuthResponse(response);
        });
    };

    /** Update default hours assignment (admin only) */
    const updateDefaultHoursAssignment = async (
        token: string,
        body: IDefaultHoursAssignment,
        id: string,
    ): Promise<IDefaultHoursAssignment> => {
        return withAuth(token, async (currentToken) => {
            const response = await fetch(`${adminDefaultHoursUrl}/${id}`, {
                method: "PUT",
                headers: await getHeader(currentToken),
                body: JSON.stringify(body),
            });
            return handleAuthResponse(response);
        });
    };

    /** Delete default hours assignment (admin only) */
    const deleteDefaultHoursAssignment = async (token: string, id: string): Promise<void> => {
        return withAuth(token, async (currentToken) => {
            const response = await fetch(`${adminDefaultHoursUrl}/${id}`, {
                method: "DELETE",
                headers: await getHeader(currentToken),
            });
            return handleAuthResponse(response);
        });
    };

    /** Get default hours consumption for date range */
    const getDefaultHoursConsumption = async (token: string, dateFrom: number, dateTo: number): Promise<IDefaultHoursConsumption[]> => {
        return withAuth(token, async (currentToken) => {
            const response = await fetch(`${defaultHoursUrl}/consumption/${dateFrom}/${dateTo}`, {
                method: "GET",
                headers: await getHeader(currentToken),
            });
            return handleAuthResponse(response);
        });
    };

    /** Get default hours days for a specific range (e.g. whole week) with automatic token renewal */
    const getDefaultHoursWeek = async (token: string, dateFrom: number, dateTo: number): Promise<IDefaultHoursDay[]> => {
        return withTokenRenewal(
            async (currentToken: string) => {
                const response = await fetch(`${defaultHoursUrl}/week/${dateFrom}/${dateTo}`, {
                    method: "GET",
                    headers: await getHeader(currentToken),
                });

                if (!response.ok && response.status === 401) {
                    const errorData = await response.json().catch(() => ({ error: response.statusText, status: response.status }));
                    throw errorData;
                }

                return handleErrors(response) as Promise<IDefaultHoursDay[]>;
            },
            token,
            renewTokenHelper,
        );
    };

    /** Get default hours info for a specific day with automatic token renewal.
     *  Returns null when the backend responds with 404 (no default hours day yet).
     */
    const getDefaultHoursDay = async (token: string, date: number): Promise<IDefaultHoursDay | null> => {
        return withTokenRenewal(
            async (currentToken: string) => {
                const response = await fetch(`${defaultHoursUrl}/day/${date}`, {
                    method: "GET",
                    headers: await getHeader(currentToken),
                });

                // Treat 404 as "no default hours configured for this day" without showing an error dialog
                if (response.status === 404) {
                    return null;
                }

                if (!response.ok && response.status === 401) {
                    const errorData = await response.json().catch(() => ({ error: response.statusText, status: response.status }));
                    throw errorData;
                }

                // Delegate all other statuses (including errors) to the shared handler
                return handleErrors(response) as Promise<IDefaultHoursDay>;
            },
            token,
            renewTokenHelper,
        );
    };

    /** Get active default-hours configuration (default project) for the current user on a specific date
     *  with automatic token renewal.
     *  Returns null when the backend responds with 404 (no active configuration for that date).
     */
    const getDefaultHoursConfigurationForDate = async (token: string, date: number): Promise<IDefaultHoursConfiguration | null> => {
        return withTokenRenewal(
            async (currentToken: string) => {
                const response = await fetch(`${defaultHoursUrl}/configuration/${date}`, {
                    method: "GET",
                    headers: await getHeader(currentToken),
                });

                if (response.status === 404) {
                    return null;
                }

                if (!response.ok && response.status === 401) {
                    const errorData = await response.json().catch(() => ({ error: response.statusText, status: response.status }));
                    throw errorData;
                }

                return handleErrors(response) as Promise<IDefaultHoursConfiguration>;
            },
            token,
            renewTokenHelper,
        );
    };

    const getDefaultHoursConfigurationForWeek = async (
        token: string,
        dateFrom: number,
        dateTo: number,
    ): Promise<Record<string, IDefaultHoursConfiguration | null>> => {
        return withTokenRenewal(
            async (currentToken: string) => {
                const response = await fetch(`${defaultHoursUrl}/configuration/week/${dateFrom}/${dateTo}`, {
                    method: "GET",
                    headers: await getHeader(currentToken),
                });

                if (!response.ok && response.status === 401) {
                    const errorData = await response.json().catch(() => ({ error: response.statusText, status: response.status }));
                    throw errorData;
                }

                return handleErrors(response) as Promise<Record<string, IDefaultHoursConfiguration | null>>;
            },
            token,
            renewTokenHelper,
        );
    };

    /** Toggle auto/manual mode for a specific day with automatic token renewal */
    const setDefaultHoursDayMode = async (token: string, date: number, mode: "auto" | "manual"): Promise<IDefaultHoursDay> => {
        return withTokenRenewal(
            async (currentToken: string) => {
                const response = await fetch(`${defaultHoursUrl}/day/${date}/mode`, {
                    method: "PATCH",
                    headers: await getHeader(currentToken),
                    body: JSON.stringify({ mode }),
                });

                if (!response.ok && response.status === 401) {
                    const errorData = await response.json().catch(() => ({ error: response.statusText, status: response.status }));
                    throw errorData;
                }

                return handleErrors(response) as Promise<IDefaultHoursDay>;
            },
            token,
            renewTokenHelper,
        );
    };

    /** Override total default hours for a specific day (keeps auto behavior) with automatic token renewal */
    const overrideDefaultHoursForDay = async (token: string, date: number, totalHours: number): Promise<IDefaultHoursDay> => {
        return withTokenRenewal(
            async (currentToken: string) => {
                const response = await fetch(`${defaultHoursUrl}/day/${date}/override`, {
                    method: "PATCH",
                    headers: await getHeader(currentToken),
                    body: JSON.stringify({ total_hours: totalHours }),
                });

                if (!response.ok && response.status === 401) {
                    const errorData = await response.json().catch(() => ({ error: response.statusText, status: response.status }));
                    throw errorData;
                }

                return handleErrors(response) as Promise<IDefaultHoursDay>;
            },
            token,
            renewTokenHelper,
        );
    };

    /** Reset override back to configured default hours for a specific day with automatic token renewal */
    const resetDefaultHoursOverrideForDay = async (token: string, date: number): Promise<IDefaultHoursDay> => {
        return withTokenRenewal(
            async (currentToken: string) => {
                const response = await fetch(`${defaultHoursUrl}/day/${date}/override`, {
                    method: "DELETE",
                    headers: await getHeader(currentToken),
                });

                if (!response.ok && response.status === 401) {
                    const errorData = await response.json().catch(() => ({ error: response.statusText, status: response.status }));
                    throw errorData;
                }

                return handleErrors(response) as Promise<IDefaultHoursDay>;
            },
            token,
            renewTokenHelper,
        );
    };

    /**
     * Reset auto-update mode for a specific day.
     * Currently implemented by setting the day's mode back to "auto",
     * which instructs the backend to treat the day as automatically managed.
     */
    const resetDefaultHoursAutoUpdate = async (token: string, date: number): Promise<void> => {
        await setDefaultHoursDayMode(token, date, "auto");
    };

    /**
     * Dev helper: arms the *next* authenticated API call to behave as if the token
     * expired, then renews via Teams SSO (same path as overnight idle + add hour).
     * Does not show the session-expired page on success.
     */
    const debugSimulateTokenExpired = async (_token: string): Promise<void> => {
        setErrorMsg(undefined);
        armDebugExpiredOnNextRequest();
        console.info(
            "[debug] Armed: add or edit an hour in the week table — the save should renew the token silently without the 401 screen.",
        );
    };

    return (
        <ApiContext.Provider
            value={{
                getServerSideToken,
                getServerSideVersion,
                fetchHours,
                fetchYearly,
                fetchHasHours,
                getProjects,
                getCompanies,
                createCompany,
                getCompanyByName,
                getUsers,
                updateUserAppSettings,
                setNewUser,
                getUser,
                getTimeEntriesBetween,
                getProjectHourTotals,
                submitTimeEntry,
                updateTimeEntry,
                updateDescription,
                deleteTimeEntry,
                resetDefaultHoursForTimeEntry,
                fetchProjects,
                fetchProject,
                deleteProject,
                getProjectSettings,
                getProjectById,
                updateProjectSettings,
                createProject,
                importHours,
                getDefaultHoursConfig,
                getDefaultHoursConfigForUser,
                createDefaultHoursAssignment,
                updateDefaultHoursAssignment,
                deleteDefaultHoursAssignment,
                getDefaultHoursConsumption,
                getDefaultHoursWeek,
                getDefaultHoursDay,
                setDefaultHoursDayMode,
                overrideDefaultHoursForDay,
                resetDefaultHoursOverrideForDay,
                resetDefaultHoursAutoUpdate,
                getDefaultHoursConfigurationForDate,
                getDefaultHoursConfigurationForWeek,
                debugSimulateTokenExpired,
            }}
        >
            {children}
        </ApiContext.Provider>
    );
}
